import { action, computed, makeObservable, observable } from "mobx"
import type { IListResponse, FetchOptions, FetchSearchParams, IOrder, IFilter } from "../types"
import { GenericListSettings } from "./GenericListSettings"

export interface IGenericListConfiguration {
  /** Optional unique id for this list. */
  id?: string;

  /** Endpoint to use for loading list of data. */
  endpoint: string;

  /** Endpoint to use for loading a single item. */
  endpointGetSingle?: string;

  /** Set in case this list required configuration for endpoint postfix. */
  requiredEndpointPostfix?: boolean;

  /** List with order options. */
  orderList?: IOrder[];
  
  /** List with filter options. */
  filterList?: IFilter[];

  /** Page number for pagination. */
  page?: number;

  /** Page size for pagination. */
  pageSize?: number;
}

/** Generic implementation to support lists from different endpoints supporting Knowmax LinqUtility ListRequest.
 *  Can be inherited or used in combination with GenericListHook, @see useList. */
export class GenericList<T = unknown> {

  configuration: IGenericListConfiguration;

  private _settingsUpdated: boolean = false;
  private _loadOptions?: FetchOptions;

  constructor(configuration: IGenericListConfiguration) {

    makeObservable(this, {
      ready: computed,
      id: observable,
      setId: action,
      settings: observable,
      setSettings: action,
      loadingSingle: observable,
      setLoadingSingle: action,
      loaded: observable,
      setLoaded: action,
      loading: observable,
      setLoading: action,
      error: observable,
      setError: action,
      orderList: observable,
      setOrderList: action,
      order: observable,
      setOrder: action,
      orderExpression: computed,
      deleted: observable,
      setDeleted: action,
      filterList: observable,
      setFilterList: action,
      filter: observable,
      setFilter: action,
      search: observable,
      setSearch: action,
      page: observable,
      setPage: action,
      pageSize: observable,
      setPageSize: action,
      list: observable,
      setList: action,
      totalCount: observable,
      setTotalCount: action,
      endpointPostFix: observable,
      setEndpointPostFix: action,
      hash: computed,
      currentHash: observable,
      setCurrentHash: action,
      selectedItem: observable,
      selectedItemTag: observable,
      setSelectedItem: action,
      load: action,
      loadSingle: action
    });

    this.configuration = configuration;

    this.id = configuration.id;
    this.orderList = configuration.orderList ?? [];
    this.order = configuration.orderList?.find(o => o.default === true);
    this.filterList = configuration.filterList ?? [];

    this.list = [];
    this.page = configuration.page ?? 1;
    this.pageSize = configuration.pageSize ?? 10;
    this.totalCount = 0;
  }

  /** Set when this generic list is ready for use. Essentially this means settings configuration is ready. */
  get ready() {
    return this.settings.ready;
  }

  /** Unique id representing this list. Will be provided to methods from settings when called to determine active list. Optional due to backward compatibility. */
  id: string | undefined;
  setId(value?: string) {
    this.id = value;
  }

  /** Settings for all generic lists. */
  settings: GenericListSettings = new GenericListSettings();
  /** Usually set from value provided GenericListSettinsProvider. */
  setSettings(value: GenericListSettings) {
    this._settingsUpdated = true;
    this.settings = value;
  }

  /** Set while loading a single item */
  loadingSingle: boolean = false;
  setLoadingSingle(value: boolean) {
    this.loadingSingle = value;
  }

  /** Set after initially loading data. */
  loaded: boolean = false;
  setLoaded(value: boolean) {
    this.loaded = value;
  }

  /** Set while loading data. */
  loading: boolean = false;
  setLoading(value: boolean) {
    this.loading = value;
  }

  error?: Error | string;
  setError(value?: Error | string) {
    this.error = value;
  }

  /** Available options for order. */
  orderList: IOrder[];
  setOrderList(value: IOrder[]) {
    this.orderList = value;
  }

  /** Selected order expression. Set to IOrder from orderList or string order expression. */
  order?: IOrder | string;
  setOrder(value?: IOrder | string) {
    this.order = value;
  }

  /** Server expression based on current order setting. */
  get orderExpression() {
    return this.order ? typeof this.order !== 'string' ? `${this.order.field}${this.order.descending === true ? ' desc' : ''}` : this.order : '';
  }

  deleted: boolean = false;
  setDeleted(value: boolean) {
    this.deleted = value;
  }

  /** List with all filter definitions available for this list. */
  filterList: IFilter[];
  setFilterList(value: IFilter[]) {
    this.filterList = value;
  }

  /** Filter to use while loading data. */
  filter?: string;
  setFilter(value?: string) {
    this.filter = value;
  }

  /** Query to be used while loading data. */
  search?: string;
  setSearch(value?: string) {
    this.search = value;
  }

  /** Current selected page. 1-based. */
  page: number;
  setPage(value: number) {
    this.page = value;
  }

  /** Number of items per page. */
  pageSize: number;
  setPageSize(value: number) {
    this.pageSize = value;
  }

  /** List containing actual page of data. */
  list: T[];
  setList(value: T[]) {
    this.list = value;
  }

  /** Total number of items available. */
  totalCount: number;
  setTotalCount(value: number) {
    this.totalCount = value;
  }

  /** Will be used as post fix for currently configured endpoint. */
  endpointPostFix?: string;
  /** Set to postfix for current endpoint. For example the value "abc" will result in "/abc" postfix after currently configured endpoint.  */
  setEndpointPostFix(value?: string) {
    this.endpointPostFix = value;
  }

  /** Dynamic hash representing current state of component. Use as dependecy to trigger effects */
  get hash() {
    return `id=${this.id}&p=${this.page}&ps=${this.pageSize}&o=${this.orderExpression}&f=${this.filter}&s=${this.search}${this.deleted ? '&d=true' : ''}&t=${this.settings.token}&epf=${this.endpointPostFix}`;
  }

  /** Hash at the moment of last update of list with server data. */
  currentHash?: string;
  setCurrentHash(value: string) {
    this.currentHash = value;
  }

  /** Set when hash was updated. Rather use hash as dependecy to trigger effects. */
  get hashUpdated() {
    return this.hash !== this.currentHash;
  }

  /** Set to selected item for specific purpose. */
  selectedItem?: T;
  /** Tag to identify purpose of selected item.. */
  selectedItemTag?: string;
  /** Selects item for specific purpose (not edit or delete). */
  setSelectedItem(value?: T, tag?: string) {
    this.selectedItem = value;
    this.selectedItemTag = value ? tag : undefined;
  }

  /** Load current page of items.
   * @param reload If true, will reload current page of items using original options used on inital load.
   * @param options Options to use while loading data. If not provided, will use options used on inital load only in case of reload.
   */
  async load(reload: boolean = false, options?: FetchOptions) {
    if (this.configuration.requiredEndpointPostfix === true && (!this.endpointPostFix || this.endpointPostFix.trim() === '')) {
      this.setList([]);
      this.setTotalCount(0);
      this.setLoaded(false);
    } else if (this.hashUpdated || reload) {
      try {
        try {
          this.checkSettings();
          this.setCurrentHash(this.hash);
          this.setError(undefined);
          this.setLoading(true);

          // On reload use options from initial load in case no new options are provided.
          if (reload && !options && this._loadOptions) {
            options = this._loadOptions;
          } else if (!reload) {
            this._loadOptions = options;
          }

          const endpoint = (this.configuration.requiredEndpointPostfix === true && this.endpointPostFix && this.endpointPostFix.trim() !== '') ?
            `${this.configuration.endpoint}/${this.endpointPostFix}` :
            this.configuration.endpoint; const method = options?.method ?? 'post';

          const searchparams: FetchSearchParams = Array.isArray(options?.searchParams)
            ? [...options.searchParams]
            : { ...(options?.searchParams as Record<string, string | number | boolean>) };

          // Dynamically add deleted parameter if this.deleted is true
          if (this.deleted) {
            if (Array.isArray(searchparams)) {
              (searchparams as Array<Array<string | number | boolean>>).push(['deleted', 'true']);
            } else {
              (searchparams as Record<string, string | number | boolean>).deleted = 'true';
            }
          }

          const response = await this.settings.onFetch<IListResponse<T>>(endpoint, {
            searchParams: searchparams,//options?.searchParams,
            method: method,
            headers: options?.headers ?? this.settings.onGetHeaders(this.settings.token, this.settings.language, undefined, this.id),
            // Make sure we never post json when not post was specified as method
            json: method === 'post' ? options?.json ?? {
              count: true,
              take: this.pageSize,
              skip: (this.page - 1) * this.pageSize,
              orderBy: this.orderExpression,
              filter: this.filter,
              search: this.search
            } : undefined
          }, this.id);

          this.setList(response.value ?? []);
          this.setTotalCount(response.count ?? 0);
          this.setLoaded(true);
        } catch (e) {
          if (e instanceof Error) {
            this.setError(e);
          }
          this.setLoaded(false);
        }
      } finally {
        this.setLoading(false);
      }
    }
  }

  /** Load single item identified by id. 
   * @param id Id of item to load.
   * @param options Options to use while loading data.
   * @param endpoint Endpoint to use while loading data. If not provided, will use endpointGetSingle or endpoint from configuration.
  */
  async loadSingle(id: string | number, options?: FetchOptions, endpoint?: string) {
    this.setError(undefined);

    try {
      this.checkSettings();
      this.setLoadingSingle(true);

      try {
        return await this.settings.onFetch<T>(`${endpoint ?? this.configuration.endpointGetSingle ?? this.configuration.endpoint}/${id}`, {
          searchParams: options?.searchParams,
          method: options?.method ?? 'get',
          headers: options?.headers ?? this.settings.onGetHeaders(this.settings.token, this.settings.language, undefined, this.id)
        }, this.id);
      } catch (e) {
        if (e instanceof Error) {
          this.setError(e);
        }
      }
    } finally {
      this.setLoadingSingle(false);
    }
  }

  private checkSettings() {
    if (!this._settingsUpdated) {
      console.warn(`Settings not set for generic list with endpoint ${this.configuration.endpoint}`)
    }
  }
}